import React, { useRef, useEffect } from 'react';

import { ScrollArea } from '../ui/scroll-area';
import { cn } from '../../lib/utils';
import { useConversation } from './ConversationContext';
import { ConversationMessage } from './ConversationMessage';

export interface ConversationMessagesProps {
  className?: string;
  bottomPadding?: number;
  onContentBehind?: (isBehind: boolean) => void;
}

export const ConversationMessages = ({ className, bottomPadding = 0, onContentBehind }: ConversationMessagesProps) => {
  const { messages } = useConversation();
  const viewportRef = useRef<HTMLDivElement>(null);
  const sentinelRef = useRef<HTMLDivElement>(null);

  // Auto-scroll to bottom when messages change
  useEffect(() => {
    const viewport = viewportRef.current;
    if (viewport) {
      viewport.scrollTop = viewport.scrollHeight;
    }
  }, [messages]);

  // IntersectionObserver on sentinel to detect content behind the input
  useEffect(() => {
    const sentinel = sentinelRef.current;
    const viewport = viewportRef.current;
    if (!sentinel || !viewport || !onContentBehind) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry) onContentBehind(!entry.isIntersecting);
      },
      { root: viewport, threshold: 0, rootMargin: `0px 0px -${bottomPadding}px 0px` },
    );
    observer.observe(sentinel);

    return () => observer.disconnect();
  }, [onContentBehind, bottomPadding]);

  return (
    <ScrollArea ref={viewportRef} className={cn('h-full', className)}>
      <div className="px-6">
        {messages.map((message) => (
          <ConversationMessage key={message.id} message={message} />
        ))}
        <div ref={sentinelRef} className="h-px" />
      </div>
      <div style={{ height: bottomPadding + 8 }} />
    </ScrollArea>
  );
};
